import { useMemo } from 'react'
import type { Dayjs } from 'dayjs'
import type { InfiniteData } from '@tanstack/react-query'

import type { TimelinePhoto, TimelinePhotoPage } from './useTimelineStream'
import { capturedDayFromNanos } from './util'

export interface TimelinePhotoRowV2 {
  content: 'row'
  key: string
  day: number
  elements: TimelinePhoto[]
  fractions: number[]
  firstInDay: boolean
  incomplete?: boolean
}

export interface TimelineHeaderV2 {
  content: 'header'
  day: number
  key: string
}

export interface TimelineDividerV2 {
  content: 'divider'
  key: string
  timestampNanos: bigint
}

export type TimelineRowDataV2 =
  | TimelinePhotoRowV2
  | TimelineHeaderV2
  | TimelineDividerV2

export function useGroupByDayAndRowV2(
  columns: number,
  queryData: InfiniteData<TimelinePhotoPage> | undefined,
  preserveAspect: boolean,
) {
  const result = useMemo(() => {
    if (!queryData) {
      return []
    }

    const rows = groupByDayAndRowV2(columns, queryData, preserveAspect)
    return rows
  }, [queryData, columns, preserveAspect])

  return result
}

export function groupByDayAndRowV2(
  columns: number,
  timelineData: InfiniteData<TimelinePhotoPage>,
  preserveAspect: boolean = false,
): TimelineRowDataV2[] {
  const rows: TimelineRowDataV2[] = []
  const defWidth = 16
  const defHeight = 9
  const THRESHOLD = 0.1
  const aspect = 1 / columns

  const fractionFn = preserveAspect
    ? (media: TimelinePhoto) =>
        aspect / ((media.height || defHeight) / (media.width || defWidth))
    : () => aspect

  let currentDay = -Infinity
  let currentFraction = 0

  const finalizeRow = (row: TimelinePhotoRowV2) => {
    // Final adjustment, check if last line needs to be stretched out or not
    row.incomplete = true
    if (currentFraction > 1 || Math.abs(1 - currentFraction) <= THRESHOLD) {
      row.fractions = row.fractions.map(f => f / currentFraction)
      row.incomplete = false
    } else if (preserveAspect) {
      row.fractions.push(1 - currentFraction)
    } else {
      for (let i = row.fractions.length; i < Math.round(columns); i++) {
        row.fractions.push(row.fractions[0])
      }
    }
  }

  const pages = timelineData.pages
  for (let i = 0; i < pages.length; i++) {
    const { photos, incompleteDaySplit } = pages[i]
    const incompleteDay = incompleteDaySplit !== undefined

    for (let j = 0; j < photos.length; j++) {
      const media = photos[j]

      const capturedDay = capturedDayFromNanos(media.timestampNanos)

      // Add divider for incomplete day
      // This reduces jank by preventing reflows due to photos being positioned
      // differently when more data is loaded by anchoring them to a divider
      if (j === 0 && incompleteDay) {
        currentDay = capturedDay
        rows.push({
          content: 'divider',
          key: `d-${i}`,
          timestampNanos: media.timestampNanos,
        })
        rows.push({
          content: 'row',
          elements: [],
          fractions: [],
          key: '',
          day: capturedDay,
          firstInDay: true,
        })
        currentFraction = 0
      }

      // New day group
      if (currentDay !== capturedDay) {
        currentDay = capturedDay

        // Finalize last row in group
        if (rows.length) {
          const currentRow = rows[rows.length - 1] as TimelinePhotoRowV2
          finalizeRow(currentRow)
          currentFraction = 0
        }
        rows.push({
          content: 'header',
          day: capturedDay,
          key: `h-${capturedDay}`,
        })
        rows.push({
          content: 'row',
          elements: [],
          fractions: [],
          key: '',
          day: capturedDay,
          firstInDay: true,
        })
      }

      let currentRow = rows[rows.length - 1] as TimelinePhotoRowV2
      const fraction = fractionFn(media)
      const nextFraction = currentFraction + fraction

      // New row
      if (
        currentRow.elements.length &&
        1 - currentFraction < Math.abs(1 - nextFraction)
      ) {
        // Adjust current row to use 100% fraction
        const cf = currentFraction
        currentRow.fractions = currentRow.fractions.map(f => (f / cf) * 100)

        // New row
        currentRow = {
          content: 'row',
          elements: [],
          fractions: [],
          key: '',
          day: capturedDay,
          firstInDay: false,
        }
        rows.push(currentRow)
        currentFraction = 0
      }

      // Finally add photo
      currentRow.elements.push(media)
      currentRow.fractions.push(fraction)
      currentFraction += fraction

      if (currentRow.elements.length === 1) {
        currentRow.key = media.timestampNanos.toString()
      }
    }
  }

  if (rows.length) {
    const row = rows[rows.length - 1]

    if (row.content === 'row') {
      finalizeRow(row)
    }
  }

  return rows
}
